import { toRelativeUrl } from '@okta/okta-auth-js'
import { useOktaAuth } from '@okta/okta-react'
import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { setActiveRoute } from '../redux/reducers/appSettingsReducer'
import { useAppDispatch } from '../redux/store'
import oktaService from '../services/oktaService'
import { logDiagnosticEvent } from '../services/diagnosticLogger'

function ProtectedRoute() {
  const { oktaAuth, authState } = useOktaAuth()
  const dispatch = useAppDispatch()
  const path = useLocation().pathname
  const authFlowInProgress = useRef(false)

  useEffect(() => {
    if (!authState) {
      return
    }

    const userId = authState.idToken?.claims?.preferred_username

    if (!authState?.isAuthenticated) {
      if (authFlowInProgress.current || oktaService.silentAcquisitionInProgress) {
        return
      }

      authFlowInProgress.current = true

      const originalUri = toRelativeUrl(window.location.href, window.location.origin)
      oktaAuth.setOriginalUri(originalUri)

      // Attempt a silent token acquisition using the existing Portal Okta session.
      // Portal and Planner share the same Okta issuer, so after a Portal login the
      // browser already holds a valid Okta SSO session cookie. getWithoutPrompt()
      // exchanges that session for Planner tokens entirely in a hidden iframe —
      // no user-visible redirect, no second login prompt.
      //
      // Only fall back to a redirect when the silent attempt fails
      // (no active session, third-party cookies blocked, network error, etc.).
      // The fallback is routed through oktaService.triggerOktaRedirect() so that
      // the PKCE code_verifier cannot be clobbered by competing redirect flows
      // (tokenManager "expired" listener, 401 interceptors, etc.).
      // httpAuth.ts handles only expired tokens / 401 responses that arise
      // *after* Planner is fully loaded.
      oktaService.silentAcquisitionInProgress = true
      logDiagnosticEvent({ eventType: 'silent_acquisition_attempted', path, userId, level: 'info' })
      oktaAuth.token
        .getWithoutPrompt({ redirectUri: oktaAuth.options.redirectUri })
        .then(tokenResponse => {
          // getWithoutPrompt() does NOT auto-populate the token manager — we must
          // store the tokens explicitly so that subsequent API calls find them.
          oktaAuth.tokenManager.setTokens(tokenResponse.tokens)
          // The Okta React SDK watches the token manager and will update authState
          // asynchronously, which triggers a re-render of ProtectedRoute as
          // authenticated — no further navigation is needed here.
          logDiagnosticEvent({
            eventType: 'silent_acquisition_succeeded',
            path,
            userId,
            outcome: 'success',
            level: 'info',
          })
          logDiagnosticEvent({
            eventType: 'token_renewal_succeeded',
            path,
            userId,
            outcome: 'success',
            level: 'info',
          })
        })
        .catch(() => {
          // Silent acquisition failed — fall back to a full redirect login using
          // Planner's own Okta app registration. This avoids sending the user to
          // the Portal homepage (which uses Portal's client ID and redirect URI),
          // which would cause an OAuthError for accounts not assigned to that app.
          // Route through the centralized guard to prevent PKCE race conditions.
          logDiagnosticEvent({
            eventType: 'silent_acquisition_failed',
            path,
            userId,
            outcome: 'failed',
            level: 'warn',
          })
          logDiagnosticEvent({
            eventType: 'okta_redirect_initiated',
            path,
            userId,
            outcome: 'redirected',
            level: 'warn',
          })
          oktaService.triggerOktaRedirect(originalUri)
        })
        .finally(() => {
          oktaService.silentAcquisitionInProgress = false
          authFlowInProgress.current = false
        })

      return
    }

    if (oktaAuth.isLoginRedirect()) {
      logDiagnosticEvent({
        eventType: 'okta_redirect_returned',
        path,
        userId,
        outcome: 'success',
        level: 'info',
      })
    }

    // The Okta redirect round-trip has completed successfully — the user is
    // authenticated. Clear the in-flight guard so the localStorage
    // `isRedirecting` flag (set by oktaService.beginRedirect() before leaving
    // for Okta) does not stay stuck as 'true' and silently no-op every
    // subsequent re-auth attempt (silent renewal, Resync button, expired
    // token redirect). releaseRedirectGuard() is idempotent and safe to
    // call even when no redirect was in flight (e.g. silent acquisition).
    oktaService.releaseRedirectGuard()

    logDiagnosticEvent({ eventType: 'app_loaded', path, userId, outcome: 'success', level: 'info' })

    dispatch(setActiveRoute(path))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState?.isAuthenticated])

  if (!authState || !authState?.isAuthenticated) {
    return null
  }

  return <Outlet />
}

export default ProtectedRoute
